import { FC } from 'react';
import { styled, Typography } from '@mui/material';
import Datepicker from '@/components/_common/Datepicker/Datepicker';
import SelectDropdownControl from '@/components/Focus/common/SelectDropdownControl';
import { FocusTheme } from '@/components/Focus/configs/theme';
import { PolarisIcon } from '@/exports/columnCommonComponents';

type IncrementalLaunchReleaseTimeControlProps = {
  disabled?: boolean;
  onTimeChange: (value: Date | null) => void;
  onTimezoneChange: (value: string) => void;
  timezone: string;
  timezoneOptions: string[];
  value: Date | null;
};

const IncrementalLaunchReleaseTimeControl: FC<IncrementalLaunchReleaseTimeControlProps> = ({
  disabled = false,
  onTimeChange,
  onTimezoneChange,
  timezone,
  timezoneOptions,
  value
}) => {
  return (
    <ReleaseTimeControlContainer>
      <ReleaseTimeFieldGroup>
        <ReleaseTimeLabel variant="caption">
          Release Time<Asterisk>*</Asterisk>
        </ReleaseTimeLabel>
        <StyledDatepickerWrapper $disabled={disabled}>
          <Datepicker
            bare
            selected={value}
            onChange={(date) => {
              if (disabled) {
                return;
              }

              onTimeChange(date);
            }}
            showTimeSelect
            showTimeSelectOnly
            timeIntervals={15}
            timeCaption="Time"
            dateFormat="h:mm aa"
            icon={<PolarisIcon icon="calendar" />}
            disabled={disabled}
          />
        </StyledDatepickerWrapper>
      </ReleaseTimeFieldGroup>
      <SelectDropdownControl minWidth={56} onChange={onTimezoneChange} options={timezoneOptions} value={timezone} />
    </ReleaseTimeControlContainer>
  );
};

export default IncrementalLaunchReleaseTimeControl;

const ReleaseTimeControlContainer = styled('div')`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const ReleaseTimeFieldGroup = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  margin-bottom: 20px;
`;

const ReleaseTimeLabel = styled(Typography)`
  color: ${({ theme }) => (theme as FocusTheme).palette.primary.main};
  font-weight: ${({ theme }) => theme.typography.fontWeightLight};
`;

const Asterisk = styled('span')`
  color: ${({ theme }) => theme.palette.secondary.main};
  margin-left: 1px;
`;

const StyledDatepickerWrapper = styled('div')<{ $disabled: boolean }>`
  align-items: center;
  display: flex;
  height: 40px;
  opacity: ${({ $disabled }) => ($disabled ? 0.38 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
  width: 130px;

  & .react-datepicker-wrapper {
    height: 100%;
    width: 100%;
  }

  & .react-datepicker__input-container {
    align-items: center;
    background-color: ${({ theme }) => (theme as FocusTheme).palette.neutral[80]};
    border-radius: ${({ theme }) => theme.shape.borderRadius}px;
    display: flex;
    height: 100%;
    padding: 0 ${({ theme }) => theme.spacing(2)};
    position: relative;
    width: 100%;
  }

  & .date-picker-wrapped {
    background: transparent;
    border: 0;
    color: ${({ theme }) => theme.palette.common.white};
    font-size: ${({ theme }) => theme.typography.body1};
    font-weight: ${({ theme }) => theme.typography.fontWeightLight};
    height: 100%;
    line-height: 40px;
    margin: 0;
    outline: 0;
    padding: 0 ${({ theme }) => theme.spacing(4)} 0 0 !important;
    text-align: left;
    width: 100%;

    &::placeholder {
      color: ${({ theme }) => theme.palette.common.white};
      font-size: ${({ theme }) => theme.typography.body1};
      opacity: 1;
    }

    &:focus,
    &:focus-visible {
      outline: 0;
    }
  }

  & .react-datepicker__calendar-icon {
    height: 20px;
    padding: 0;
    width: 20px;
  }

  & .datepicker-icon {
    color: ${({ theme }) => (theme as FocusTheme).palette.neutral[20]};
    left: auto;
    pointer-events: none;
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
  }
`;
